// Core imports
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

// Component imports
import { Col, Divider, Dropdown, Input, Menu, Row, Select, Space, Tooltip, Typography } from 'antd';
import { PlusOutlined, MoreOutlined, DeleteOutlined, EditOutlined, WarningOutlined } from '@ant-design/icons';

// Misc imports
import { useCreateCSATSectionMutation, useDeleteCSATSectionMutation, useFetchCSATSectionsQuery, useUpdateCSATSectionMutation } from '../../store/CSAT/CSATApi';
import { truncate } from '../../utils/string/truncate';
import groupBy from '../../utils/array/groupBy';

const CSATSectionsDropdown = ({
  value,
  selectedSectionIds,
  onSelectCSATSectionSetId,
  onChange,
  ...props
}) => {
  const { orgId } = useParams();

  const createSectionRef = useRef(null);
  const addNewTempState = useRef(false);

  const [isCreateSectionInputOpen, setIsCreateSectionInputOpen] = useState(false);
  const [isEditSectionInputOpen, setIsEditSectionInputOpen] = useState(false);
  const [selectedCSATSection, setSelectedCSATSection] = useState(null);
  const [selectedSectionTitle, setSelectedSectionTitle] = useState(null);
  const [isSelectDropdownOpen, setIsSelectDropdownOpen] = useState(false);
  const [createTitleInput, setCreateTitleInput] = useState('');
  const [editTitleInput, setEditTitleInput] = useState('');

  const [createCSATSection] = useCreateCSATSectionMutation();
  const [updateCSATSection] = useUpdateCSATSectionMutation();
  const [deleteCSATSection] = useDeleteCSATSectionMutation();
  const { data: CSATSections } = useFetchCSATSectionsQuery({
    orgId,
    urlParams: {
      search: '',
      status: ''
    }
  });

  const handleAddNewClick = (e) => {
    e.stopPropagation();
    onCreateInputChange('');
    setIsSelectDropdownOpen(true);
    setIsCreateSectionInputOpen(!isCreateSectionInputOpen);
    addNewTempState.current = true;
  };

  const handleSelectBlur = (e) => {
    setTimeout(() => {
      const clickedOutside = e?.relatedTarget?.classList
        ? Array.from(e?.relatedTarget?.classList ?? []).some(className => className.includes('modal'))
        : false;

      if(clickedOutside && !addNewTempState.current) {
        handleDropdownVisibility(false);
      }

      addNewTempState.current = false;
    }, [300]);
  };

  const handleCreateCSATSection = async (sectionData) => {
    const valuesToSubmit = {
      ...sectionData
    };

    const isCreatedSuccessfully = await createCSATSection({ orgId, data: valuesToSubmit }).unwrap();

    if(isCreatedSuccessfully) {
      setIsCreateSectionInputOpen(false);
      onCreateInputChange('');
    }
  };

  const handleEditCSATSection = async (sectionData) => {
    const valuesToSubmit = { ...sectionData };
    const isUpdatedSuccessfully = await updateCSATSection({ orgId, CSATSectionId: selectedCSATSection?.id, data: valuesToSubmit }).unwrap();

    if (isUpdatedSuccessfully) {
      setIsEditSectionInputOpen(false);
      setSelectedCSATSection(null);
      setEditTitleInput('');
      setSelectedSectionTitle(valuesToSubmit.title);
    }
  };

  const handleDeleteCSATSection = async (sectionId) => {
    const isDeletedSuccessfully = await deleteCSATSection({ orgId, CSATSectionId: sectionId }).unwrap();
    if(isDeletedSuccessfully) {
      if (sectionId === selectedCSATSection?.id) {
        setSelectedCSATSection(null);
      }
    }
  };

  const handleSelect = (title) => {
    setSelectedSectionTitle(title);
    const currentSectionId = CSATSections?.filter(section => section.status !== 'DELETED').find(section => section.title === title)?.id;

    if(onSelectCSATSectionSetId && currentSectionId) {
      onSelectCSATSectionSetId(currentSectionId);
    }
    if (onChange) {
      onChange(title);
    }
    setIsSelectDropdownOpen(false);
  };

  const handleDropdownVisibility = (isOpen) => {
    setTimeout(() => setIsSelectDropdownOpen(isOpen), 0);
  };

  const onSectionCreate = async (event, sectionTitle) => {
    event.stopPropagation();
    await handleCreateCSATSection({
      title: sectionTitle
    });
  };

  const onCreateInputChange = (value) => {
    setCreateTitleInput(value);
  };

  const onEditInputChange = (value) => {
    setEditTitleInput(value);
  };

  const onSectionEdit = async (event, section) => {
    event.stopPropagation();

    if (event.target.value === section.title) {
      setIsEditSectionInputOpen(null);
      return;
    }

    if (selectedCSATSection?.id === section.id) {
      const updatedSection = { ...section, title: event.target.value };
      await handleEditCSATSection(updatedSection);
    }
  };

  useEffect(() => {
    if(value) {
      setSelectedSectionTitle(value);
    }
  }, [value]);

  useEffect(() => {
    if(isCreateSectionInputOpen) {
      setTimeout(() => {
        createSectionRef?.current?.input?.focus();
      }, 100);
    }
  }, [isCreateSectionInputOpen]);

  useEffect(() => {
    if(!isSelectDropdownOpen) {
      setCreateTitleInput('');
      setSelectedCSATSection(null);
      setIsEditSectionInputOpen(false);
      setIsCreateSectionInputOpen(false);
    }
  }, [isSelectDropdownOpen]);

  const doesCreatedTitleAlreadyExists = useCallback(() => {
    return CSATSections?.filter(section => section.status !== 'DELETED')?.find(status => status.title === createTitleInput);
  }, [CSATSections, createTitleInput]);

  const doesEditedTitleAlreadyExists = useCallback((title) => {
    return CSATSections?.filter(section => section.status !== 'DELETED')?.find(status => status.title === editTitleInput);
  }, [CSATSections, editTitleInput]);

  const sectionItemMenu = (section) => (
    <Menu>
      <Menu.Item key="1" icon={<EditOutlined />}
        onClick={(event) => {
          setIsSelectDropdownOpen(true);
          event?.domEvent?.stopPropagation?.();
          setSelectedCSATSection(section);
          setIsEditSectionInputOpen(true);
        }}
      >
        {t('Rename Section')}
      </Menu.Item>
      <Menu.Item
        key="2"
        icon={<DeleteOutlined />}
        onClick={async (event) => {
          setIsSelectDropdownOpen(true);
          event?.domEvent?.stopPropagation?.();
          await handleDeleteCSATSection(section.id);
        }}
      >
        {t('Delete Section')}
      </Menu.Item>
    </Menu>
  );

  return (
    <Fragment>
      <Select
        allowClear
        showSearch
        onSearch={(value) => {
          setIsSelectDropdownOpen(true);
        }}
        getPopupContainer={triggerNode => triggerNode.parentElement}
        onBlur={handleSelectBlur}
        className="csat-select-dropdown w-100"
        onClick={(e) => {
          // This onClick handles the main toggle for clicking the section select
          // The issue is that when clicking the rename/delete section buttons
          // the select dropdown toggles itself. Any stopPropagation or preventDefault
          // didn't work
          // It checks both parents in case the user clicked on Text based element
          // Disadvantage: Doesnt respond to outside click events to close it

          const firstParentContainer = e?.target?.parentElement;
          const isFirstParentSelectContainer = firstParentContainer?.classList
            ? Array.from(firstParentContainer?.classList ?? []).some(className => className.includes('ant-select'))
            : false;

          if(isFirstParentSelectContainer) {
            handleDropdownVisibility(!isSelectDropdownOpen);
            return;
          }

          const secondParentContainer = firstParentContainer.parentElement;
          const isSecondParentSelectContainer = secondParentContainer?.classList
            ? Array.from(secondParentContainer?.classList ?? []).some(className => className.includes('ant-select'))
            : false;

          if(isSecondParentSelectContainer) {
            handleDropdownVisibility(!isSelectDropdownOpen);
          }

        }}
        {...props}
        placeholder={t('Enter the section category')}
        optionLabelProp="label"
        onDropdownVisibleChange={(isOpen) => {
          if(!isOpen) {
            setIsEditSectionInputOpen(false);
            setIsCreateSectionInputOpen(false);
          }
        }}
        open={isSelectDropdownOpen}
        value={selectedSectionTitle}
        onChange={handleSelect}
        dropdownRender={menu => (
          <Fragment>
            <Row className="p-3" gutter={[16, 16]}>
              <Col xs={24}>
                <Space direction="horizontal" className="d-flex justify-content-between w-100">
                  <Typography.Text className="text-secondary">
                    {t('Rating Sections')}
                  </Typography.Text>
                  <Typography.Link
                    onClick={handleAddNewClick}
                  >
                    <PlusOutlined /> {t('Add new')}
                  </Typography.Link>
                </Space>
              </Col>
              <Col xs={24} hidden={!isCreateSectionInputOpen}>
                <Input
                  ref={createSectionRef}
                  size="middle"
                  status={doesCreatedTitleAlreadyExists() && 'error'}
                  placeholder="Enter new section title"
                  autoFocus
                  onChange={e => {
                    onCreateInputChange(e.currentTarget.value);
                  }}
                  value={createTitleInput}
                  maxLength={50}
                  showCount
                  onClick={(event) => {
                    event?.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setIsCreateSectionInputOpen(false);
                      onCreateInputChange('');
                    }
                  }}
                  onPressEnter={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if(doesCreatedTitleAlreadyExists()){
                      return;
                    }
                    if (event.target.value !== '') {
                      onSectionCreate(event, event.target.value);
                    }
                  }}
                  onBlur={() => {
                    onCreateInputChange('');
                  }}
                />
                {doesCreatedTitleAlreadyExists() && (
                  <Typography.Text type="danger" className="mt-1 d-block">
                    {t('Title already exists.')}
                  </Typography.Text>
                )}
              </Col>
              <Col xs={24} style={{ maxHeight: isCreateSectionInputOpen ? '150px' : '200px' }}>
                {menu}
              </Col>
            </Row>
          </Fragment>
        )}
      >
        {
          CSATSections?.length > 0 && Object.entries(groupBy(Object.values(CSATSections), 'status'))
            ?.sort(([statusA], [statusB]) => {
              if (statusA !== 'DELETED') {
                return -1;
              }
              if (statusB !== 'DELETED') {
                return 1;
              }
              return 0;
            })
            ?.map(([status, sections]) => (
              <Select.OptGroup
                key={status}
                label={
                  <Fragment>
                    {
                      status === 'DELETED' && (
                        <Space direction="vertical" className="w-100">
                          <Divider className="m-0" />
                        </Space>
                      )
                    }
                  </Fragment>
                }
              >
                {
                  sections?.map(currentSection => {
                    const sectionAlreadySelected = selectedSectionIds.includes(currentSection?.id);
                    return (
                      <Select.Option
                        key={currentSection.id}
                        value={currentSection.title}
                        disabled={currentSection.status === 'DELETED' || sectionAlreadySelected}
                        className={currentSection.status === 'DELETED' ? 'cursor-default text-dec-line-through' : ''}
                        label={
                          <Typography.Text className={currentSection.status === 'DELETED' ? 'text-dec-line-through' : ''}
                          >
                            <Tooltip
                              title={currentSection?.title?.length >= 30 && currentSection.title}
                            >
                              {truncate( t(currentSection.title), 30, '...')}
                            </Tooltip>
                            <Tooltip
                              title={
                                currentSection?.status === 'DELETED' && <Row>
                                  <Col xs={24}>
                                    <Typography.Text className="text-secondary fs-base">
                                      {t('This Custom Section has been deleted. Please replace it wherever it is used.')}
                                    </Typography.Text>
                                  </Col>
                                </Row>
                              }
                            >
                              <WarningOutlined className="text-warning ml-1" hidden={currentSection?.status !== 'DELETED'}/>
                            </Tooltip>
                          </Typography.Text>
                        }
                      >
                        <Fragment>
                          <Space className="d-flex justify-content-between" direction="horizontal">
                            {
                              currentSection?.status === 'DELETED' ? (
                                <Fragment>
                                  <Typography.Text>
                                    <Tooltip
                                      title={currentSection?.title?.length >= 30 && currentSection.title}
                                    >
                                      {truncate( t(currentSection.title), 30, '...')}
                                    </Tooltip>
                                    <Tooltip
                                      title={
                                        currentSection?.status === 'DELETED' && <Row>
                                          <Col xs={24}>
                                            <Typography.Text className="text-secondary fs-base">
                                              {t('This Custom Section has been deleted. Please replace it wherever it is used.')}
                                            </Typography.Text>
                                          </Col>
                                        </Row>
                                      }
                                    >
                                      <WarningOutlined className="text-warning ml-1" hidden={currentSection?.status !== 'DELETED'}/>
                                    </Tooltip>
                                  </Typography.Text>
                                </Fragment>
                              ) : (
                                <Fragment>
                                  {
                                    selectedCSATSection?.id === currentSection?.id && isEditSectionInputOpen ? (
                                      <Fragment>
                                        <Input
                                          size="small"
                                          placeholder="Edit section title"
                                          autoFocus
                                          status={doesEditedTitleAlreadyExists() && editTitleInput !== selectedCSATSection?.title && 'error'}
                                          defaultValue={currentSection?.title || ''}
                                          onChange={e => {
                                            onEditInputChange(e.currentTarget?.value);
                                          }}
                                          maxLength={50}
                                          showCount
                                          onClick={(event) => {
                                            event.stopPropagation();
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              setIsEditSectionInputOpen(null);
                                              onEditInputChange(currentSection?.title);
                                            }
                                          }}
                                          onPressEnter={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            if(doesEditedTitleAlreadyExists() && editTitleInput !== selectedCSATSection?.title){
                                              return;
                                            }
                                            if (event.target.value !== '') {
                                              onSectionEdit(event, currentSection);
                                            }
                                          }}
                                          onBlur={(event) => {
                                            onEditInputChange(currentSection?.title);
                                            setIsEditSectionInputOpen(null);
                                          }}
                                        />
                                        {doesEditedTitleAlreadyExists() && editTitleInput !== selectedCSATSection?.title && (
                                          <Typography.Text type="danger" className="mt-1 d-block">
                                            {t('Title already exists.')}
                                          </Typography.Text>
                                        )}
                                      </Fragment>
                                    ) : (
                                      <Typography.Text>
                                        <Tooltip
                                          className={sectionAlreadySelected ? 'opacity-40' : ''}
                                          title={currentSection?.title?.length >= 30 && currentSection.title}
                                        >
                                          {truncate( t(currentSection.title), 30, '...')}
                                        </Tooltip>
                                      </Typography.Text>
                                    )
                                  }
                                </Fragment>
                              )
                            }

                            <Dropdown
                              disabled={currentSection?.status === 'DELETED'}
                              overlay={() => {
                                return sectionItemMenu(currentSection);
                              }}
                              onOpenChange={() => setIsSelectDropdownOpen(true)}
                              trigger={['click']}>
                              <Typography.Link
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                className={classNames('fs-xl pointer-cursor', {
                                  'text-disabled': currentSection?.status === 'DELETED'
                                })}
                              >
                                <MoreOutlined />
                              </Typography.Link>
                            </Dropdown>
                          </Space>
                        </Fragment>
                      </Select.Option>
                    );
                  })
                }
              </Select.OptGroup>
            ))
        }
      </Select>
    </Fragment>
  );
};

CSATSectionsDropdown.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
  onSelectCSATSectionSetId: PropTypes.func,
  selectedSectionIds: PropTypes.arrayOf('string')
};

export default CSATSectionsDropdown;
